import { type FC, useCallback, useState } from "react";

import { Button } from "../../common/Button";
import { type BlackboardInstance } from "../blackboardModels";
import { isStaleCacheBreak } from "./helpers";

// ---------------------------------------------------------------------------
// IdentityPromptSection — editable identity prompt + danger zone (kill).
// Persisted via PATCH /user/instances/:id. The cache-break badge surfaces when
// an edit will miss the system prompt cache on the next wake.
// ---------------------------------------------------------------------------

export const IdentityPromptSection: FC<{
  instance: BlackboardInstance;
  onKill: () => void;
  killing: boolean;
  onSaved?: () => void;
}> = ({ instance, onKill, killing, onSaved }) => {
  const [identityPrompt, setIdentityPrompt] = useState(
    instance.identityPrompt ?? "",
  );
  const [saving, setSaving] = useState(false);
  const isDirty = identityPrompt !== (instance.identityPrompt ?? "");

  const handleSaveIdentityPrompt = useCallback(async () => {
    setSaving(true);
    try {
      const resp = await fetch(`/user/instances/${instance.id}`, {
        body: JSON.stringify({ identityPrompt: identityPrompt.trim() || null }),
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        method: "PATCH",
      });
      if (resp.ok) {
        // Sync local state so isDirty clears without requiring a parent re-render.
        setIdentityPrompt(identityPrompt.trim());
        onSaved?.();
      }
    } finally {
      setSaving(false);
    }
  }, [instance.id, identityPrompt, onSaved]);

  if (instance.status === "killed") {
    return null;
  }

  return (
    <>
      <div className="flex flex-col gap-2 p-4">
        <div className="flex items-center gap-2">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-violet-700/90 dark:text-violet-300/80">
            [ IDENTITY PROMPT ]
          </p>
          {!isDirty && isStaleCacheBreak(instance) && (
            <span className="border border-amber-500/50 bg-amber-400/5 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-amber-700 dark:border-amber-400/40 dark:text-amber-300">
              [ CACHE BREAK NEXT WAKE ]
            </span>
          )}
        </div>
        <textarea
          className="min-h-[80px] w-full resize-y border border-slate-300 bg-white/60 p-2 font-mono text-[11px] leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-violet-400/60 focus:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-violet-400/30 dark:border-slate-700/60 dark:bg-slate-900/60 dark:text-slate-100 dark:placeholder:text-slate-600 dark:focus:bg-slate-900"
          placeholder="Describe this actor's personality, role, or instructions…"
          value={identityPrompt}
          onChange={(e) => setIdentityPrompt(e.target.value)}
        />
        {isDirty && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSaveIdentityPrompt()}
              loading={saving}
              loadingText="Saving…"
            >
              Save
            </Button>
            <p className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300">
              [ UNSAVED · NEXT WAKE MISSES SYSTEM CACHE ]
            </p>
          </div>
        )}
      </div>

      <div className="relative m-4 border border-rose-500/30 bg-rose-500/[0.03] p-4">
        <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-rose-700/90 dark:text-rose-300/80">
          [ DANGER ZONE ]
        </p>
        <Button
          variant="danger"
          size="sm"
          onClick={onKill}
          loading={killing}
          loadingText="Killing..."
        >
          Kill Instance
        </Button>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-rose-700/80 dark:text-rose-300/60">
          Permanent termination. Cannot be undone.
        </p>
      </div>
    </>
  );
};
